export const MOLDOVA_TZ = "Europe/Chisinau";
export const BUSY_KEYS_MODE = "local-match";

const __fmtCache = new Map();

export function getFmt(locale, timeZone, mode) {
   const key = `${locale}|${timeZone}|${mode}`;
   let fmt = __fmtCache.get(key);
   if (fmt) return fmt;

   if (mode === "date") {
      fmt = new Intl.DateTimeFormat(locale, {
         timeZone,
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
      });
   } else if (mode === "time") {
      fmt = new Intl.DateTimeFormat(locale, {
         timeZone,
         hour12: false,
         hour: "2-digit",
         minute: "2-digit",
      });
   } else if (mode === "date-long") {
      fmt = new Intl.DateTimeFormat(locale, {
         timeZone,
         weekday: "long",
         year: "numeric",
         month: "long",
         day: "numeric",
      });
   } else if (mode === "datetime-sec") {
      fmt = new Intl.DateTimeFormat(locale, {
         timeZone,
         hour12: false,
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
         second: "2-digit",
      });
   } else {
      fmt = new Intl.DateTimeFormat(locale, {
         timeZone,
         hour12: false,
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
      });
   }

   __fmtCache.set(key, fmt);
   return fmt;
}

export function partsObj(formatter, dateLike) {
   const out = {};
   const parts = formatter.formatToParts(new Date(dateLike));
   for (const p of parts) {
      if (p.type !== "literal") out[p.type] = p.value;
   }
   return out;
}

export function partsInTZ(dateLike, timeZone = MOLDOVA_TZ) {
   const p = partsObj(getFmt("en-GB", timeZone, "datetime-sec"), dateLike);
   const get = (t) => Number(p[t] ?? 0);
   return {
      y: get("year"),
      m: get("month"),
      d: get("day"),
      H: get("hour"),
      M: get("minute"),
      S: get("second"),
   };
}

export function localDateStrTZ(date, tz = MOLDOVA_TZ) {
   const p = partsObj(getFmt("en-GB", tz, "date"), date);
   const day = p.day ?? "01";
   const month = p.month ?? "01";
   const year = p.year ?? "1970";
   return `${year}-${month}-${day}`;
}

export const formatDateKeyInChisinau = localDateStrTZ;

export function ymdStrInTZ(dateLike, timeZone = MOLDOVA_TZ) {
   const { y, m, d } = partsInTZ(dateLike, timeZone);
   return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function tzOffsetMinutesAt(tsMs, timeZone = MOLDOVA_TZ) {
   const p = partsObj(getFmt("en-GB", timeZone, "datetime-sec"), tsMs);
   const y = Number(p.year);
   const m = Number(p.month);
   const d = Number(p.day);
   const H = Number(p.hour);
   const M = Number(p.minute);
   const S = Number(p.second);
   const asUTC = Date.UTC(y, m - 1, d, H, M, S);
   return (asUTC - tsMs) / 60000;
}

export function hhmmInTZ(dateLike, timeZone = MOLDOVA_TZ) {
   const { H, M } = partsInTZ(dateLike, timeZone);
   return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
}

export const formatTimeLabelInChisinau = hhmmInTZ;

export function toUtcIsoFromMoldova(localDateObj, timeStrHHMM) {
   const [hh, mm] = (timeStrHHMM || "00:00").split(":").map(Number);
   const utcGuess = Date.UTC(
      localDateObj.getFullYear(),
      localDateObj.getMonth(),
      localDateObj.getDate(),
      hh,
      mm,
      0,
      0,
   );
   const offMin = tzOffsetMinutesAt(utcGuess, MOLDOVA_TZ);
   let fixedUtcMs = utcGuess - offMin * 60000;
   // ziua schimbării orei: decalajul se ia în momentul real al acestei ore, nu în cel „ghicit”
   // (altfel pe 25.10 orele de după 01:00 ieșeau cu o oră mai târziu); în celelalte zile — același rezultat
   const offReal = tzOffsetMinutesAt(fixedUtcMs, MOLDOVA_TZ);
   if (offReal !== offMin) fixedUtcMs = utcGuess - offReal * 60000;
   return new Date(fixedUtcMs).toISOString();
}

export function isoForDbMatchLocalHour(isoUtcFromMoldova) {
   const base = new Date(isoUtcFromMoldova);
   // „local-match”: ora Chișinăului a momentului real, scrisă ca UTC. Înainte era base + decalaj —
   // același lucru în orice zi, în afară de ziua schimbării orei (acolo ieșea până la 2 ore mai târziu).
   const wall = partsInTZ(base, MOLDOVA_TZ);
   const shifted = new Date(Date.UTC(wall.y, wall.m - 1, wall.d, wall.H % 24, wall.M, wall.S, 0));

   const parts = partsObj(getFmt("en-GB", MOLDOVA_TZ, "datetime"), shifted);
   const Y = parts.year;
   const Mo = parts.month;
   const Da = parts.day;
   const HH = parts.hour;
   const MM = parts.minute;

   const offMin2 = tzOffsetMinutesAt(shifted.getTime(), MOLDOVA_TZ);
   const sign = offMin2 >= 0 ? "+" : "-";
   const abs = Math.abs(offMin2);
   const offHH = String(Math.floor(abs / 60)).padStart(2, "0");
   const offMM = String(abs % 60).padStart(2, "0");

   return `${Y}-${Mo}-${Da}T${HH}:${MM}:00${sign}${offHH}:${offMM}`;
}

export function localKeyFromTs(dateOrMs, tz = MOLDOVA_TZ) {
   const tsMs =
      typeof dateOrMs === "number"
         ? dateOrMs
         : new Date(dateOrMs).getTime();
   if (!Number.isFinite(tsMs)) return "";

   const parts = partsObj(getFmt("en-GB", tz, "datetime"), tsMs);
   const Y = parts.year;
   const Mo = parts.month;
   const Da = parts.day;
   const HH = parts.hour;
   const MM = parts.minute;
   return `${Y}-${Mo}-${Da}|${HH}:${MM}`;
}

export const localKeyForIso = (iso) =>
   localKeyFromTs(new Date(iso).getTime(), MOLDOVA_TZ);

export const localKeyForDateAndTime = (localDateObj, hhmm) =>
   `${localDateStrTZ(localDateObj, MOLDOVA_TZ)}|${hhmm}`;

export function readRawDateTimeParts(value) {
   const text = String(value || "").trim();
   if (!text) return null;

   const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]+)(\d{2}):(\d{2})/);
   if (!match) return null;

   const [, year, month, day, hour, minute] = match;
   return { year, month, day, hour, minute };
}

export function localKeyFromStoredRaw(value) {
   const raw = readRawDateTimeParts(value);
   if (!raw) return "";
   return `${raw.year}-${raw.month}-${raw.day}|${raw.hour}:${raw.minute}`;
}

/**
 * Availability / booking wall-clock ISO for local-match storage.
 * "2026-07-22" + "15:00" → "2026-07-22T15:00:00.000Z"
 * Digits are the schedule time as returned by /availability — no TZ shift.
 */
export function buildLocalMatchIso(dayYYYYMMDD, timeHHMM) {
   const text = String(dayYYYYMMDD ?? "").trim();
   const dayMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (!dayMatch) return null;
   const timeText = String(timeHHMM ?? "").trim();
   const timeMatch = timeText.match(/^(\d{1,2}):(\d{2})/);
   if (!timeMatch) return null;
   const hh = String(
      Math.max(0, Math.min(23, Number(timeMatch[1]) || 0)),
   ).padStart(2, "0");
   const mm = String(
      Math.max(0, Math.min(59, Number(timeMatch[2]) || 0)),
   ).padStart(2, "0");
   return `${dayMatch[1]}-${dayMatch[2]}-${dayMatch[3]}T${hh}:${mm}:00.000Z`;
}

/** Calendar day from a DatePicker value (local Y-M-D, no timezone conversion). */
export function calendarDayStr(date) {
   if (!date || Number.isNaN(new Date(date).getTime())) return "";
   const d = new Date(date);
   const y = d.getFullYear();
   const m = String(d.getMonth() + 1).padStart(2, "0");
   const day = String(d.getDate()).padStart(2, "0");
   return `${y}-${m}-${day}`;
}

/** Slot key "YYYY-MM-DD|HH:mm" from wall-clock / local-match ISO digits. */
export function wallClockSlotKeyFromIso(iso) {
   const raw = localKeyFromStoredRaw(iso);
   if (raw) return raw;
   if (BUSY_KEYS_MODE === "local-match") {
      return busyLocalKeyFromStored(iso);
   }
   return localKeyFromTs(new Date(iso).getTime(), MOLDOVA_TZ);
}

export function wallClockDayFromIso(iso) {
   const key = wallClockSlotKeyFromIso(iso);
   return key ? key.split("|")[0] : "";
}

/**
 * Payload startTimes for auto-assign.
 * UI keeps wall-clock digits from /availability (07:00 = 07:00).
 * On submit only: same calculated path as SAddProg
 * (true Moldova instant → local-match DB ISO).
 */
export function startTimesForAutoAssign(selectedIsos = []) {
   return (selectedIsos || [])
      .map((iso) => {
         const raw = readRawDateTimeParts(iso);
         if (!raw) return String(iso || "");

         const dateObj = new Date(
            Number(raw.year),
            Number(raw.month) - 1,
            Number(raw.day),
            12,
            0,
            0,
            0,
         );
         const trueMoldovaIso = toUtcIsoFromMoldova(
            dateObj,
            `${raw.hour}:${raw.minute}`,
         );

         if (BUSY_KEYS_MODE === "local-match") {
            return isoForDbMatchLocalHour(trueMoldovaIso);
         }
         return trueMoldovaIso;
      })
      .filter(Boolean);
}

export function busyLocalKeyFromStored(st) {
   const rawKey = localKeyFromStoredRaw(st);
   if (rawKey) return rawKey;

   const d = new Date(st);
   if (Number.isNaN(d.getTime())) return "";

   if (BUSY_KEYS_MODE === "local-match") {
      const offMin = tzOffsetMinutesAt(d.getTime(), MOLDOVA_TZ);
      const base = new Date(d.getTime() - offMin * 60000);
      return localKeyFromTs(base.getTime(), MOLDOVA_TZ);
   }
   return localKeyFromTs(d.getTime(), MOLDOVA_TZ);
}
